import { MenuItem } from '@maxpreps/apiserviceclients/apiModels';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import PageNav, {
  IPageNavLink
} from '@maxpreps/shared/corecomponents/PageNav/PageNav';
import { replaceOriginKeys } from '@maxpreps/shared/functions/primitiveHelpers/urlHelpers';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { IConfigurableLink } from '@maxpreps/shared/models/configurableLink';
import React from 'react';
import { ThemeProvider, useTheme } from 'styled-components';
import SeasonPickerButton from './SeasonPicker/SeasonPickerButton';
import TeamSmartAnchor from './TeamSmartAnchor';

const getRawUrl = (publicUrl, adminUrl, adminLoginUrl, isAdmin) => {
  if (!isAdmin) return publicUrl;
  return adminLoginUrl ?? adminUrl;
};

// Check if the submenu contains at least one valid URL that isn't the same as the header's URL
const doesMenuContainOtherLinks = (items, href) =>
  items.some(item => item?.href !== href && item?.as !== href);

const parseSubMenuItem = (
  subMenuItem: MenuItem,
  isAdmin: boolean
): IConfigurableLink | undefined => {
  const { text, adminLoginUrl, adminUrl, publicUrl } = subMenuItem;

  const rawUrl = getRawUrl(publicUrl, adminUrl, adminLoginUrl, isAdmin);
  if (!rawUrl) return;
  const href = replaceOriginKeys(rawUrl);

  return {
    text,
    as: 'a',
    asOptions: {
      href,
      anchorComponent: TeamSmartAnchor
    }
  };
};

const getSubMenuItems = (
  subMenuItems: MenuItem[],
  isAdmin: boolean
): IConfigurableLink[] =>
  subMenuItems
    ?.map(menuItem => parseSubMenuItem(menuItem, isAdmin))
    .filter(menuItem => !!menuItem) as IConfigurableLink[];

const parseMenuItem = (
  menuItem: MenuItem,
  isAdmin: boolean
): IPageNavLink | undefined => {
  const { text, subMenu, adminLoginUrl, adminUrl, publicUrl } = menuItem;

  const rawUrl = getRawUrl(publicUrl, adminUrl, adminLoginUrl, isAdmin);

  if (!rawUrl && !subMenu) return;

  const href = replaceOriginKeys(rawUrl);
  const subItems = getSubMenuItems(subMenu, isAdmin);

  const isSubMenuVisible =
    subItems?.length && doesMenuContainOtherLinks(subItems, href);

  if (!href && !isSubMenuVisible) return;

  return {
    text,
    href,
    subItems: isSubMenuVisible ? subItems : undefined,
    anchorComponent: TeamSmartAnchor
  };
};

const getMenuItems = (
  teamMenuData: MenuItem[],
  isAdmin: boolean
): IPageNavLink[] =>
  teamMenuData
    .map(menuItem => parseMenuItem(menuItem, isAdmin))
    .filter(menuItem => !!menuItem) as IPageNavLink[];

interface ITeamNavBarProps {
  navId: string;
}

const TeamNavBar: React.FC<ITeamNavBarProps> = ({ navId }) => {
  const teamContext = useTeamContext();
  const isAdmin = useIsAdmin();
  const theme = useTheme();
  const { teamMenuData } = teamContext;

  const pageNavLinks = getMenuItems(teamMenuData, isAdmin);

  const pageNavTheme = {
    ...theme,
    colors: {
      ...theme.colors,
      other: {
        ...theme.colors.other,
        link: theme.colors.nav.header
      }
    }
  };

  return (
    <ThemeProvider theme={pageNavTheme}>
      <PageNav
        pageNavLinks={pageNavLinks}
        navId={navId}
        leftContent={<SeasonPickerButton />}
      />
    </ThemeProvider>
  );
};

export default TeamNavBar;
